import React, { useState, useMemo, useEffect } from 'react';
import { Box, Typography, Loader, Button } from '@lucid/lego';
import { useStore } from '../../../store/store';
import { useGetLabels } from '../../../hooks/useLabels';
import { convertToContentfulLocale } from '../../../utils/tools';
import Table from './Table/Table';
import Configurator from '../../../components/Configurator/Configurator';
import { useProspectsTable } from './hooks/useProspectsTable';
import { useProspectUrlState } from './hooks/useProspectUrlState';
import styles from './Prospects.module.scss';
import Checkout from '../../../components/Checkout/Checkout';
import { InventoryTableRow } from 'src/components/Configurator/utils';
import { useGetQuoteById } from '../../../api/services/lupp/quotes/quotes';

const Prospects: React.FC = () => {
  const theme = useStore((state) => state.theme);
  const locale = useStore((state) => state.locale);
  const selectedQuote = useStore((state) => state.selectedQuote);
  const clearSelectedProspect = useStore((state) => state.clearSelectedProspect);
  const clearSelectedQuote = useStore((state) => state.clearSelectedQuote);
  const [showVehicleDetails, setShowVehicleDetails] = useState(false);

  // Labels
  const {
    data: labels,
    isLoading: labelsLoading,
    error: labelsError,
  } = useGetLabels({
    id: '6dhtpdHCYn1jTZ1cRAXtJR',
    locale: convertToContentfulLocale(locale),
  });

  // URL state management
  const {
    selectedProspectId,
    activeTab,
    showConfigurator,
    checkoutParams,
    hasInitialProspectInUrl,
    selectedProspectData,
    onProspectSelect,
    onTabChange,
    onCreateQuotation,
    onCreateProspect,
    onBackToProspects,
    onCheckoutNavigate,
    onCheckoutBack,
    onBackToProspectDetails,
  } = useProspectUrlState();

  // Table state management - disable when in configurator/checkout view
  const {
    data: prospectsData,
    totalCount: prospectsTotalCount,
    isLoading,
    isFetching,
    error,
    currentPage,
    pageSize,
    searchQuery,
    debouncedSearch,
    sortField,
    sortOrder,
    onPageChange,
    onPageSizeChange,
    onSearchChange,
    onSort,
  } = useProspectsTable({
    enabled: !labelsLoading && !labelsError && !showConfigurator,
  });

  // Merge table data with single prospect view data
  const tableData = useMemo(() => {
    if (hasInitialProspectInUrl && selectedProspectData?.prospectDetails) {
      return [selectedProspectData.prospectDetails];
    }
    return prospectsData;
  }, [prospectsData, hasInitialProspectInUrl, selectedProspectData]);

  const totalCount = useMemo(() => {
    if (hasInitialProspectInUrl && selectedProspectData?.prospectDetails) {
      return 1;
    }
    return prospectsTotalCount;
  }, [hasInitialProspectInUrl, selectedProspectData, prospectsTotalCount]);

  // Configurator handlers
  const handleBackToProspects = () => {
    setShowVehicleDetails(false);
    clearSelectedProspect();
    onBackToProspects();
  };

  const handleConfigureVehicle = (shortCode: string) => {
    // TODO: Implement short code vehicle configuration
    console.error('Configure vehicle with short code:', shortCode);
  };

  const handleCheckoutBack = () => {
    setShowVehicleDetails(false);
    onCheckoutBack();
  };

  useEffect(() => {
    clearSelectedQuote();
  }, [clearSelectedQuote]);

  const handleBackToInventory = () => {
    clearSelectedQuote();
    onBackToProspectDetails('quotations');
  };

  const prospectId = selectedProspectId ?? '';
  const firstName = selectedProspectData?.prospectDetails?.prospectFirstName ?? '';
  const lastName = selectedProspectData?.prospectDetails?.prospectLastName ?? '';
  const prospectName = firstName || lastName ? `${firstName} ${lastName}`.trim() : '';
  const quoteId = selectedQuote?.quote_identifier ?? '';

  const { data: quoteData, isLoading: isQuoteLoading } = useGetQuoteById({
    prospectId,
    quoteId,
    enabled: !!prospectId && !!quoteId,
  });

  const detailsVehicle = { vin: quoteData?.vin ?? '' } as InventoryTableRow;

  const prospectDetails = {
    prospectId,
    prospectName,
    quoteId,
  };

  if (selectedQuote) {
    return (
      <Checkout
        vehicle={detailsVehicle}
        checkoutData={quoteData ?? null}
        isLoading={isQuoteLoading}
        onBack={handleBackToInventory}
        dataTestId={`${quoteId}-checkout`}
        prospectDetails={prospectDetails}
        docusignStatus={quoteData?.docusignStatus}
        currentStatus={quoteData?.currentStatus}
      />
    );
  }

  // Render configurator view
  if (showConfigurator) {
    return (
      <Box>
        {!showVehicleDetails && (
          <Box mb={16}>
            <Button
              type="tertiary"
              color="neutral"
              theme={theme}
              dataTestId="back-to-prospects-button"
              gaComponent="prospects"
              label="Back to Prospects"
              iconLeft={{ category: 'arrows', name: 'arrow-left' }}
              handleClick={handleBackToProspects}
            />
          </Box>
        )}
        {!showVehicleDetails && (
          <>
            <Typography variant="headline" size="small" color="primary-1" theme={theme}>
              {prospectName ? `Quotation for ${prospectName}` : 'Quotation'}
            </Typography>
            <Typography variant="body" size="medium" color="gray-3" theme={theme}>
              Saved quotations can be reviewed in prospect details.
            </Typography>
          </>
        )}
        <Box pt={showVehicleDetails ? 0 : 24}>
          <Configurator
            onConfigureVehicle={handleConfigureVehicle}
            dataTestId="prospect-configurator"
            onDetailsViewChange={setShowVehicleDetails}
            checkoutParams={checkoutParams}
            onCheckoutNavigate={onCheckoutNavigate}
            onCheckoutBack={handleCheckoutBack}
            prospectDetails={prospectDetails}
          />
        </Box>
      </Box>
    );
  }

  // Render loading state
  if (labelsLoading || isLoading) {
    return (
      <Box>
        <Typography variant="headline" size="small" color="primary-1" theme={theme}>
          {labels?.title}
        </Typography>
        <Typography variant="body" size="medium" color="gray-3" theme={theme}>
          {labels?.description}
        </Typography>
        <Box className={styles.loaderContainer}>
          <Loader size={48} />
        </Box>
      </Box>
    );
  }

  // Render error state
  if (labelsError) {
    return (
      <Box>
        <Box pt={24}>
          <Typography variant="body" size="large" theme={theme} color="error-1">
            {labels?.error_loading_labels}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error && !searchQuery && !debouncedSearch && !isFetching) {
    return (
      <Box>
        <Typography variant="headline" size="small" color="primary-1" theme={theme}>
          {labels?.title}
        </Typography>
        <Typography variant="body" size="medium" color="gray-3" theme={theme}>
          {labels?.description}
        </Typography>
        <Box pt={24}>
          <Typography variant="body" size="large" theme={theme} color="error-1">
            {labels?.error_loading_leads}
          </Typography>
        </Box>
      </Box>
    );
  }

  // Render table view
  return (
    <Box>
      <Typography variant="headline" size="small" color="primary-1" theme={theme}>
        {labels?.title}
      </Typography>
      <Typography variant="body" size="medium" color="gray-3" theme={theme}>
        {labels?.description}
      </Typography>
      <Box pt={24} className={styles.loadingContainer}>
        {isFetching && (
          <Box className="fetchingOverlay">
            <Loader size={48} />
          </Box>
        )}
        <Table
          theme={theme}
          data={error ? [] : tableData}
          totalCount={error ? 0 : totalCount}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          labels={labels || {}}
          searchQuery={searchQuery}
          onSearchChange={onSearchChange}
          onCreateQuotation={onCreateQuotation}
          onCreateProspect={onCreateProspect}
          sortColumn={sortField}
          sortDirection={sortOrder}
          onSort={onSort}
          selectedProspectId={selectedProspectId}
          onProspectSelect={onProspectSelect}
          activeTab={activeTab}
          onTabChange={onTabChange}
        />
      </Box>
    </Box>
  );
};

export default Prospects;
